import { useState, useEffect, useCallback } from "react";
import {
  Newspaper,
  Share2,
  ThumbsUp,
  ThumbsDown,
  Bookmark,
} from "lucide-react";
import { toggleThumb } from "../../../../../api/feed";
import { usePopup } from "../../../../contexts/PopupContext";
import ShareModal from "../../../../Navigation/Sidebar/ShareModal";

/**
 * SharedArticleCard — renders a shared article in chat threads
 * with screenshot, like/dislike/bookmark, share, and
 * bidirectional thumb sync with the ArticleDetailPanel sidebar.
 *
 * Sync uses CustomEvent (same pattern as public-citation:interact):
 *   Card → Sidebar:  "shared-article:card-interact"
 *   Sidebar → Card:  "shared-article:sidebar-interact"
 */

const BLUE = "#1c9dea";

const ActionPill = ({ active, label, onClick, children }) => (
  <button
    type="button"
    aria-pressed={!!active}
    aria-label={label}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(e);
    }}
    className="inline-flex items-center justify-center rounded-full h-8 w-8 border backdrop-blur-sm transition-all duration-200 active:scale-95 cursor-pointer"
    style={{
      borderColor: active ? "rgba(28,157,234,0.5)" : "rgba(100,116,139,0.25)",
      background: active ? "rgba(28,157,234,0.1)" : "transparent",
    }}
  >
    {children}
  </button>
);

const SharedArticleCard = ({ msg, userUuid, onClickArticle }) => {
  const { setIsPopUpOpen, setPopUpComponent } = usePopup();

  const meta = msg.article_meta || {};
  const snap = meta.article_snapshot || {};
  const docId = meta.document_id;
  const isOwn = msg.sender_uuid === userUuid;

  const title = snap.title || "Untitled Article";
  const source = snap.source || "";
  const targetType = snap.target_type || "";
  const target = snap.target || "";
  const screenshot = snap.screenshot || "";
  const screenshotFull = snap.screenshot_full || "";
  const timestamp = snap.timestamp || "";
  const aptName = snap.apt_name || "";

  const [localLike, setLocalLike] = useState(false);
  const [localDislike, setLocalDislike] = useState(false);
  const [localCatalog, setLocalCatalog] = useState(false);
  const [imgError, setImgError] = useState(false);

  /* ── Sidebar → Card sync ── */
  useEffect(() => {
    if (!docId) return;
    const handler = (e) => {
      const d = e.detail || {};
      if (d.documentId !== docId) return;
      setLocalLike(d.like);
      setLocalDislike(d.dislike);
      setLocalCatalog(d.catalog);
    };
    window.addEventListener("shared-article:sidebar-interact", handler);
    return () =>
      window.removeEventListener("shared-article:sidebar-interact", handler);
  }, [docId]);

  /* ── Card → Sidebar sync ── */
  const notifySidebar = useCallback(
    (state) => {
      window.dispatchEvent(
        new CustomEvent("shared-article:card-interact", {
          detail: { documentId: docId, ...state },
        }),
      );
    },
    [docId],
  );

  const handleLike = () => {
    const next = !localLike;
    setLocalLike(next);
    toggleThumb({ document_id: docId, action: "like" });
    let newDislike = localDislike;
    if (localDislike) {
      setLocalDislike(false);
      toggleThumb({ document_id: docId, action: "dislike" });
      newDislike = false;
    }
    notifySidebar({ like: next, dislike: newDislike, catalog: localCatalog });
  };

  const handleDislike = () => {
    const next = !localDislike;
    setLocalDislike(next);
    toggleThumb({ document_id: docId, action: "dislike" });
    let newLike = localLike;
    if (localLike) {
      setLocalLike(false);
      toggleThumb({ document_id: docId, action: "like" });
      newLike = false;
    }
    notifySidebar({ like: newLike, dislike: next, catalog: localCatalog });
  };

  const handleCatalog = () => {
    const next = !localCatalog;
    setLocalCatalog(next);
    toggleThumb({ document_id: docId, action: "catalog" });
    notifySidebar({ like: localLike, dislike: localDislike, catalog: next });
  };

  const handleShare = (e) => {
    e.stopPropagation();
    const articleSnapshot = {
      title: snap.title || "",
      url: snap.url || "",
      source: snap.source || "",
      screenshot: snap.screenshot || "",
      screenshot_full: snap.screenshot_full || "",
      target_type: snap.target_type || "",
      target: snap.target || "",
      content: (snap.content || "").slice(0, 300),
      timestamp: snap.timestamp || "",
      apt_name: snap.apt_name || "",
    };
    setPopUpComponent(
      <ShareModal
        onClose={() => setIsPopUpOpen(false)}
        contentType="article"
        documentId={docId}
        itemName={title}
        articleSnapshot={articleSnapshot}
      />,
    );
    setIsPopUpOpen(true);
  };

  const handleClick = () => {
    if (docId && onClickArticle) {
      onClickArticle(docId);
    }
  };

  const formatType = (t) => {
    if (!t) return "";
    return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const iconStyle = (active) => ({
    color: active ? BLUE : "rgb(148,163,184)",
    fill: active ? BLUE : "none",
    filter: active ? "drop-shadow(0 0 4px rgba(28,157,234,0.5))" : "none",
    transition: "all 0.2s",
  });

  const imgSrc = screenshot || screenshotFull;

  return (
    <div className="rounded-xl border border-surfaceBorder bg-dropdownBg/60 backdrop-blur-sm overflow-hidden max-w-sm">
      {/* Clickable area — opens sidebar */}
      <div
        onClick={handleClick}
        className="cursor-pointer hover:bg-btnHover/40 transition-colors"
      >
        {/* Header: icon + title + meta */}
        <div className="flex items-start gap-x-2.5 px-3.5 pt-3 pb-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-velBlue/15 mt-0.5">
            <Newspaper className="h-3.5 w-3.5 text-velBlue" strokeWidth={1.5} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-light text-mainText leading-snug line-clamp-2">
              {title}
            </p>
            <div className="flex items-center gap-x-1.5 mt-1 flex-wrap">
              {source && (
                <span className="text-[11px] font-light text-mutedText/60 truncate max-w-[120px]">
                  {source}
                </span>
              )}
              {source && (targetType || timestamp) && (
                <span className="text-[11px] text-mutedText/30">·</span>
              )}
              {targetType && (
                <span className="text-[10px] font-light px-1.5 py-0.5 rounded-full bg-velBlue/10 text-velBlue/80 whitespace-nowrap">
                  {formatType(targetType)}
                </span>
              )}
              {timestamp && (
                <>
                  <span className="text-[11px] text-mutedText/30">·</span>
                  <span className="text-[11px] font-light text-mutedText/60 whitespace-nowrap">
                    {timestamp}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Screenshot */}
        {imgSrc && !imgError && (
          <div className="px-3.5 pb-2">
            <div className="rounded-lg overflow-hidden border border-surfaceBorder/50 bg-surface/40">
              <img
                src={imgSrc}
                alt={title}
                className="w-full h-32 object-cover object-top"
                loading="lazy"
                onError={() => setImgError(true)}
              />
            </div>
          </div>
        )}

        {/* Target + APT badges */}
        {(target || aptName) && (
          <div className="px-3.5 pb-2 flex items-center gap-x-2 flex-wrap">
            {target && (
              <span className="inline-flex items-center text-[10px] font-light px-1.5 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400/80 whitespace-nowrap">
                {target}
              </span>
            )}
            {aptName && (
              <span className="inline-flex items-center text-[10px] font-light px-1.5 py-0.5 rounded-full bg-pink-500/10 text-pink-400/80 whitespace-nowrap">
                {aptName}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Personal message from sender */}
      {msg.content && (
        <div className="px-3.5 pb-2">
          <p className="text-[12px] font-light text-mutedText">
            "{msg.content}"
          </p>
        </div>
      )}

      {/* Action bar */}
      <div className="px-3.5 pb-3 pt-1.5 border-t border-surfaceBorder/30 flex items-center gap-x-2">
        <ActionPill active={localLike} label="Like" onClick={handleLike}>
          <ThumbsUp
            className="h-3.5 w-3.5"
            strokeWidth={1.5}
            style={iconStyle(localLike)}
          />
        </ActionPill>
        <ActionPill
          active={localDislike}
          label="Dislike"
          onClick={handleDislike}
        >
          <ThumbsDown
            className="h-3.5 w-3.5"
            strokeWidth={1.5}
            style={iconStyle(localDislike)}
          />
        </ActionPill>
        <ActionPill
          active={localCatalog}
          label="Bookmark"
          onClick={handleCatalog}
        >
          <Bookmark
            className="h-3.5 w-3.5"
            strokeWidth={1.5}
            style={iconStyle(localCatalog)}
          />
        </ActionPill>
        <ActionPill active={false} label="Share" onClick={handleShare}>
          <Share2
            className="h-3.5 w-3.5"
            strokeWidth={1.5}
            style={iconStyle(false)}
          />
        </ActionPill>

        <div className="flex-1" />

        <span className="text-[10px] font-light text-mutedText/50 flex items-center gap-x-1">
          <Share2 className="h-2.5 w-2.5" strokeWidth={2} />
          {isOwn ? "You" : msg.sender_name || "Unknown"}
        </span>
      </div>
    </div>
  );
};

export default SharedArticleCard;
